import L from "leaflet";
import airports from "./data/us-airports.json";

const STATE_KEY = "hydrotools:travel-planning:v1";
const DEFAULT_CONUS_LODGING = 110;
const DEFAULT_CONUS_MIE = 68;

const ORIGIN_AIRPORTS = [
  { code: "SFO", name: "San Francisco Intl", lat: 37.619806, lon: -122.374821, adj: 38 },
  { code: "SEA", name: "Seattle-Tacoma Intl", lat: 47.447943, lon: -122.310276, adj: 24 },
  { code: "HOU", name: "William P. Hobby", lat: 29.645336, lon: -95.276812, adj: 12 },
  { code: "CHS", name: "Charleston Intl", lat: 32.896159, lon: -80.038151, adj: 8 },
  { code: "BOS", name: "Boston Logan Intl", lat: 42.36197, lon: -71.0079, adj: 20 },
];

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function haversineMiles(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const aa = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const km = 6371 * (2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa)));
  return km * 0.621371;
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      lat: Number.isFinite(Number(x.lat)) ? clamp(Number(x.lat), -90, 90) : 37.7749,
      lon: Number.isFinite(Number(x.lon)) ? clamp(Number(x.lon), -180, 180) : -122.4194,
      gsaApiKey: typeof x.gsaApiKey === "string" ? x.gsaApiKey : "",
    };
  } catch {
    return { lat: 37.7749, lon: -122.4194, gsaApiKey: "" };
  }
}

function saveState(s) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(s));
  } catch {}
}

function airportTypeAdj(type) {
  if (type === "large_airport") return 24;
  if (type === "medium_airport") return 14;
  return 8;
}

function estimateFare(oneWayMiles, originAdj, destAdj) {
  const d = Math.max(0, Number(oneWayMiles) || 0);
  const seg1 = Math.min(d, 500) * 0.16;
  const seg2 = Math.min(Math.max(d - 500, 0), 1000) * 0.12;
  const seg3 = Math.max(d - 1500, 0) * 0.09;
  const oneWay = Math.max(69, 58 + seg1 + seg2 + seg3 + originAdj + destAdj);
  const roundTrip = oneWay * 1.87;
  return {
    oneWay,
    roundTrip,
    low: roundTrip * 0.72,
    high: roundTrip * 1.28,
  };
}

function nearestAirports(lat, lon, count = 3) {
  const rows = airports
    .map((a) => ({
      ...a,
      miles: haversineMiles(lat, lon, a.lat, a.lon),
    }))
    .sort((a, b) => {
      if (a.scheduled !== b.scheduled) return a.scheduled ? -1 : 1;
      return a.miles - b.miles;
    });
  return rows.slice(0, Math.max(1, count));
}

async function reverseGeocode(lat, lon) {
  const params = new URLSearchParams({
    format: "jsonv2",
    lat: String(lat),
    lon: String(lon),
    addressdetails: "1",
    zoom: "12",
  });
  const res = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Reverse geocode HTTP ${res.status}`);
  }
  const json = await res.json();
  const addr = json?.address || {};
  return {
    displayName: json?.display_name || "",
    city: addr.city || addr.town || addr.village || addr.hamlet || "",
    county: addr.county || "",
    state: addr.state || "",
    stateCode: addr.state_code || "",
    country: addr.country || "",
    countryCode: String(addr.country_code || "").toUpperCase(),
  };
}

async function fetchGsaPerDiem(city, stateCode, apiKey) {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  const cityNorm = String(city || "").trim().toUpperCase();
  const stateNorm = String(stateCode || "").trim().toUpperCase();
  if (!cityNorm || !stateNorm) {
    return {
      ok: false,
      fallback: true,
      lodging: DEFAULT_CONUS_LODGING,
      mie: DEFAULT_CONUS_MIE,
      note: "City/state unavailable for GSA city lookup. Showing current standard CONUS fallback.",
    };
  }
  if (!apiKey) {
    return {
      ok: false,
      fallback: true,
      lodging: DEFAULT_CONUS_LODGING,
      mie: DEFAULT_CONUS_MIE,
      note: "GSA API key not set. Showing current standard CONUS fallback.",
    };
  }

  const urls = [
    `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
    `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
    `https://api.gsa.gov/travel/perdiem/v2/rates/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
  ];

  for (const u of urls) {
    try {
      const res = await fetch(u);
      if (!res.ok) continue;
      const json = await res.json();
      const parsed = parseGsaRatePayload(json, month, cityNorm, stateNorm);
      if (parsed) {
        return { ok: true, ...parsed };
      }
    } catch {}
  }
  return {
    ok: false,
    fallback: true,
    lodging: DEFAULT_CONUS_LODGING,
    mie: DEFAULT_CONUS_MIE,
    note: "GSA lookup failed for this location/API key. Showing standard CONUS fallback.",
  };
}

function normalizeUsStateCode(stateCodeRaw, stateNameRaw = "") {
  const code = String(stateCodeRaw || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) return code;
  if (/^US-[A-Z]{2}$/.test(code)) return code.slice(3);
  const name = String(stateNameRaw || "").trim().toUpperCase();
  const byName = {
    ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA", COLORADO: "CO",
    CONNECTICUT: "CT", DELAWARE: "DE", "DISTRICT OF COLUMBIA": "DC", FLORIDA: "FL", GEORGIA: "GA",
    HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL", INDIANA: "IN", IOWA: "IA", KANSAS: "KS", KENTUCKY: "KY",
    LOUISIANA: "LA", MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI", MINNESOTA: "MN",
    MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV", "NEW HAMPSHIRE": "NH",
    "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND",
    OHIO: "OH", OKLAHOMA: "OK", OREGON: "OR", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
    "SOUTH DAKOTA": "SD", TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT", VIRGINIA: "VA",
    WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI", WYOMING: "WY",
  };
  return byName[name] || "";
}

function parseGsaRatePayload(json, month, cityNorm = "", stateNorm = "") {
  const rates = Array.isArray(json?.rates) ? json.rates : [];
  let best = null;
  let bestScore = -1;
  for (const rateBlock of rates) {
    if (stateNorm && String(rateBlock?.state || "").toUpperCase() !== stateNorm) {
      continue;
    }
    const entries = Array.isArray(rateBlock?.rate) ? rateBlock.rate : [];
    for (const entry of entries) {
      const city = String(entry?.city || "").trim().toUpperCase();
      const meals = firstNumber(entry, ["meals", "mie", "meals_incidental", "mealsIncidental", "me"]);
      const monthRows = Array.isArray(entry?.months?.month)
        ? entry.months.month
        : (entry?.months?.month ? [entry.months.month] : []);
      const exactMonth = monthRows.find((m) => Number(m?.number) === Number(month));
      const fallbackMonth = monthRows[0] || null;
      const lodging = Number(exactMonth?.value ?? fallbackMonth?.value);
      if (!Number.isFinite(lodging) || !Number.isFinite(meals)) {
        continue;
      }
      let score = 0;
      if (cityNorm && city && city === cityNorm) score += 10;
      if (String(entry?.standardRate || "").toLowerCase() === "false") score += 3;
      if (!city) score -= 2;
      if (score > bestScore) {
        bestScore = score;
        best = { lodging, mie: meals };
      }
    }
  }
  return best;
}

function firstNumber(obj, keys) {
  for (const key of keys) {
    const val = obj?.[key];
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export const travelPlanningTool = {
  id: "travel-planning",
  name: "Travel Planning",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let loading = false;
  let pickMode = false;
  let status = "Pick a map point and run travel lookup.";
  let result = null;
  const markerLayer = L.layerGroup();
  let marker = null;
  let mapClickHandler = null;

  function setMarker(lat, lon) {
    if (!marker) {
      marker = L.circleMarker([lat, lon], {
        radius: 7,
        color: "#22d3ee",
        weight: 2,
        fillColor: "#0891b2",
        fillOpacity: 0.55,
      });
      marker.addTo(markerLayer);
    } else {
      marker.setLatLng([lat, lon]);
    }
  }

  function pullState() {
    if (!containerEl) return;
    state.lat = clamp(Number(containerEl.querySelector("#tp-lat")?.value || state.lat), -90, 90);
    state.lon = clamp(Number(containerEl.querySelector("#tp-lon")?.value || state.lon), -180, 180);
    state.gsaApiKey = String(containerEl.querySelector("#tp-gsa-key")?.value || "").trim();
    saveState(state);
  }

  async function runLookup() {
    pullState();
    loading = true;
    status = "Resolving location, per diem, and airport metrics...";
    result = null;
    setMarker(state.lat, state.lon);
    render();
    try {
      const geocode = await reverseGeocode(state.lat, state.lon);
      const near = nearestAirports(state.lat, state.lon, 3);
      const stateCode = normalizeUsStateCode(geocode.stateCode, geocode.state);
      const isUsLike = geocode.countryCode === "US" || Boolean(stateCode);
      const city = String(geocode.city || near?.[0]?.city || "").trim();
      const gsa = isUsLike
        ? await fetchGsaPerDiem(city, stateCode, state.gsaApiKey)
        : {
          ok: false,
          fallback: true,
          lodging: DEFAULT_CONUS_LODGING,
          mie: DEFAULT_CONUS_MIE,
          note: "Location is outside U.S. CONUS lookup scope. Showing standard CONUS fallback.",
        };
      const destAirport = near[0] || null;
      const fareRows = ORIGIN_AIRPORTS.map((origin) => {
        const miles = destAirport
          ? haversineMiles(origin.lat, origin.lon, destAirport.lat, destAirport.lon)
          : null;
        const fares = Number.isFinite(miles)
          ? estimateFare(miles, origin.adj, airportTypeAdj(destAirport?.type))
          : null;
        const co2Kg = Number.isFinite(miles) ? (miles * 2 * 0.115) : null;
        const flightHours = Number.isFinite(miles) ? (miles / 500 + 1.4) : null;
        return {
          origin,
          miles,
          fares,
          co2Kg,
          flightHours,
        };
      });
      const drive = destAirport
        ? {
          miles: near[0].miles * 1.2,
          hours: (near[0].miles * 1.2) / 45,
        }
        : null;

      result = { geocode, near, gsa, fareRows, drive, destAirport };
      status = "Travel metrics updated.";
    } catch (err) {
      status = `Travel lookup failed: ${err?.message || String(err)}`;
      result = null;
    } finally {
      loading = false;
      render();
    }
  }

  function setPickMode(enabled) {
    pickMode = !!enabled;
    const map = app.map.instance;
    if (!map) return;
    if (pickMode) {
      if (!mapClickHandler) {
        mapClickHandler = (e) => {
          state.lat = clamp(Number(e.latlng?.lat), -90, 90);
          state.lon = clamp(Number(e.latlng?.lng), -180, 180);
          saveState(state);
          setMarker(state.lat, state.lon);
          setPickMode(false);
          status = "Point selected from map.";
          render();
        };
      }
      map.on("click", mapClickHandler);
      app.map.el.style.cursor = "crosshair";
    } else {
      if (mapClickHandler) map.off("click", mapClickHandler);
      app.map.el.style.cursor = "";
    }
    render();
  }

  function render() {
    if (!containerEl) return;
    const loc = result?.geocode;
    const gsa = result?.gsa;
    const near = result?.near || [];
    const rows = result?.fareRows || [];
    const locationLabel = loc
      ? `${loc.city || "(city n/a)"}, ${loc.stateCode || loc.state || ""} ${loc.countryCode ? `(${loc.countryCode})` : ""}`
      : "";

    const nearestRows = near.length
      ? near.map((a, i) => `
          <tr>
            <td>${i + 1}</td>
            <td><strong>${esc(a.iata)}</strong></td>
            <td>${esc(a.name)}</td>
            <td>${esc(a.city || "")}, ${esc(a.state || "")}</td>
            <td style="text-align:right;">${a.miles.toFixed(1)} mi</td>
          </tr>
        `).join("")
      : `<tr><td colspan="5" class="small">No airport data.</td></tr>`;

    const fareTableRows = rows.length
      ? rows.map((r) => `
          <tr>
            <td><strong>${esc(r.origin.code)}</strong></td>
            <td style="text-align:right;">${Number.isFinite(r.miles) ? r.miles.toFixed(0) : "--"} mi</td>
            <td style="text-align:right;">${r.fares ? `$${Math.round(r.fares.roundTrip).toLocaleString()}` : "--"}</td>
            <td style="text-align:right;">${r.fares ? `$${Math.round(r.fares.low).toLocaleString()}-$${Math.round(r.fares.high).toLocaleString()}` : "--"}</td>
            <td style="text-align:right;">${Number.isFinite(r.flightHours) ? `${r.flightHours.toFixed(1)} h` : "--"}</td>
            <td style="text-align:right;">${Number.isFinite(r.co2Kg) ? `${Math.round(r.co2Kg)} kg` : "--"}</td>
          </tr>
        `).join("")
      : `<tr><td colspan="6" class="small">No fare model output.</td></tr>`;

    containerEl.innerHTML = `
      <div class="card">
        <div class="lm-title">Travel Planning</div>
        <div class="small" style="margin:6px 0 10px;">
          Select a map point to fetch U.S. federal per diem context, nearest airports, and route-cost planning metrics.
        </div>
        <div class="row lm-add-row">
          <label class="small" for="tp-lat" style="min-width:40px;">Lat</label>
          <input id="tp-lat" type="number" step="0.0001" min="-90" max="90" value="${state.lat}" style="flex:1;" />
          <label class="small" for="tp-lon" style="min-width:40px;">Lon</label>
          <input id="tp-lon" type="number" step="0.0001" min="-180" max="180" value="${state.lon}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:8px;">
          <label class="small" for="tp-gsa-key" style="min-width:66px;">GSA API</label>
          <input id="tp-gsa-key" type="password" autocomplete="off" value="${esc(state.gsaApiKey)}" placeholder="Enter your GSA API key" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:8px;">
          <button id="tp-pick-map" class="btn">${pickMode ? "Click map now..." : "Pick From Map"}</button>
          <button id="tp-run" class="btn primary" ${loading ? "disabled" : ""}>${loading ? "Loading..." : "Run Travel Lookup"}</button>
        </div>
        <div class="small" style="margin-top:10px; min-height:16px;">${esc(status)}</div>
        ${locationLabel ? `<div class="small" style="margin-top:5px;">Selected location: <strong>${esc(locationLabel)}</strong></div>` : ""}
      </div>

      ${result ? `
        <div class="card" style="margin-top:8px; padding:8px 10px;">
          <div class="lm-title" style="margin:0;">GSA Per Diem</div>
          <div class="small" style="margin-top:6px;">
            Lodging: <strong>${gsa?.lodging != null ? `$${Number(gsa.lodging).toFixed(0)}` : "--"}</strong> ·
            M&IE: <strong>${gsa?.mie != null ? `$${Number(gsa.mie).toFixed(0)}` : "--"}</strong>
          </div>
          <div class="small" style="margin-top:4px;">
            ${esc(gsa?.note || (gsa?.ok ? "Source: GSA Per Diem API." : "No per diem data."))}
          </div>
        </div>

        <div class="card" style="margin-top:8px; padding:8px 10px;">
          <div class="lm-title" style="margin:0;">3 Closest Airports</div>
          <div style="overflow:auto; margin-top:6px;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead>
                <tr>
                  <th style="text-align:left;">#</th>
                  <th style="text-align:left;">IATA</th>
                  <th style="text-align:left;">Airport</th>
                  <th style="text-align:left;">City</th>
                  <th style="text-align:right;">Distance</th>
                </tr>
              </thead>
              <tbody>${nearestRows}</tbody>
            </table>
          </div>
          ${result?.drive ? `<div class="small" style="margin-top:6px;">Estimated drive to nearest airport: ${result.drive.miles.toFixed(1)} mi (~${result.drive.hours.toFixed(1)} h).</div>` : ""}
        </div>

        <div class="card" style="margin-top:8px; padding:8px 10px;">
          <div class="lm-title" style="margin:0;">Average Flight Cost Table</div>
          <div class="small" style="margin-top:4px;">
            Destination airport for pricing model: <strong>${esc(result.destAirport?.iata || "--")}</strong> ${esc(result.destAirport?.name || "")}.
          </div>
          <div style="overflow:auto; margin-top:6px;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead>
                <tr>
                  <th style="text-align:left;">Origin</th>
                  <th style="text-align:right;">Air Dist</th>
                  <th style="text-align:right;">Avg RT</th>
                  <th style="text-align:right;">Typical Range</th>
                  <th style="text-align:right;">Est Time</th>
                  <th style="text-align:right;">CO2 (RT)</th>
                </tr>
              </thead>
              <tbody>${fareTableRows}</tbody>
            </table>
          </div>
          <div class="small" style="margin-top:6px;">
            Fare values are planning estimates (distance/airport adjusted), not live quoted fares.
          </div>
        </div>
      ` : ""}
    `;

    const pickBtn = containerEl.querySelector("#tp-pick-map");
    const runBtn = containerEl.querySelector("#tp-run");
    pickBtn?.addEventListener("click", () => setPickMode(!pickMode));
    runBtn?.addEventListener("click", () => { runLookup(); });
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    app.map.instance.addLayer(markerLayer);
    setMarker(state.lat, state.lon);
    render();
  }

  function destroy() {
    setPickMode(false);
    app.map.instance.removeLayer(markerLayer);
  }

  return {
    mount,
    destroy,
  };
}
